/**
 * HypothesesTab.js — Research hypotheses registry UI.
 *
 * Phase 2 of the R-engine architecture. Browses, filters, and manages
 * lifecycle of research hypotheses stored in the research_hypotheses table
 * (migration 082). Each hypothesis carries an evidence-level grade
 * (L1_CLINICAL / L2_PRIOR / L3_HYPOTHESIS), a structured component list,
 * exclusions, warnings, references, and an optional PDF artifact.
 *
 * Lifecycle states (enforced both at DB CHECK and IPC handler level):
 *   proposed → under_review → queued_for_trial → in_trial → archived
 *                                                            ↘ invalidated
 *
 * The detail view surfaces the warnings prominently — for L3 hypotheses
 * these typically include the standard-of-care reminder that should NEVER
 * be lost when a clinician/researcher is reviewing a novel combination.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { useNavStep } from '../../lib/NavHistory';
import {
  FlaskConical, FileText, AlertTriangle, CheckCircle2, XCircle, ChevronRight,
  ArrowLeft, RefreshCw, Filter, Loader, Lock, ExternalLink, Shield,
} from 'lucide-react';

// ── Evidence-level badge colors ───────────────────────────────────────────
const EVIDENCE_BADGES = {
  L1_CLINICAL:   { bg: '#DCFCE7', color: '#166534', label: 'L1 Clinical',     icon: CheckCircle2 },
  L2_PRIOR:      { bg: '#FEF3C7', color: '#92400E', label: 'L2 Prior',        icon: Shield },
  L3_HYPOTHESIS: { bg: '#FEE2E2', color: '#991B1B', label: 'L3 Hypothesis',   icon: AlertTriangle },
};

// ── Lifecycle status badge colors ─────────────────────────────────────────
const STATUS_BADGES = {
  proposed:         { bg: '#E0F2FE', color: '#075985', label: 'Proposed' },
  under_review:     { bg: '#FEF3C7', color: '#92400E', label: 'Under Review' },
  queued_for_trial: { bg: '#FAE8FF', color: '#86198F', label: 'Queued for Trial' },
  in_trial:         { bg: '#DBEAFE', color: '#1E40AF', label: 'In Trial' },
  archived:         { bg: '#F1F5F9', color: '#475569', label: 'Archived' },
  invalidated:      { bg: '#FEE2E2', color: '#991B1B', label: 'Invalidated' },
};

// Forward transitions allowed from each state. The DB doesn't enforce this,
// but the UI guides the user through the standard workflow. (Backward
// transitions are sometimes needed too — those are surfaced as a separate
// "override" option to keep the happy-path obvious.)
const STATUS_TRANSITIONS = {
  proposed:         ['under_review', 'archived', 'invalidated'],
  under_review:     ['queued_for_trial', 'archived', 'invalidated'],
  queued_for_trial: ['in_trial', 'archived'],
  in_trial:         ['archived', 'invalidated'],
  archived:         ['under_review'],    // re-open
  invalidated:      [],                  // terminal
};

const API = () => (typeof window !== 'undefined' ? window.zignagenixAPI : null);

export default function HypothesesTab({ user }) {
  const [hypotheses, setHypotheses] = useState([]);
  const [selected, setSelected] = useState(null);   // currently-open full detail
  const [detail, setDetail]   = useState(null);     // full record with components/exclusions/warnings
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(null);
  const [filterStatus, setFilterStatus]               = useState('');
  const [filterEvidence, setFilterEvidence]           = useState('');
  const [transitioning, setTransitioning]             = useState(false);

  const perms = Array.isArray(user?.permissions) ? user.permissions : [];
  const canWrite = perms.includes('bi:create');

  // ── Load list ──────────────────────────────────────────────────────────
  const loadList = useCallback(async () => {
    const api = API();
    if (!api?.listResearchHypotheses) {
      setError('Research hypotheses API not available — restart the server to pick up the new IPC handlers.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const filters = {};
      if (filterStatus)   filters.status         = filterStatus;
      if (filterEvidence) filters.evidence_level = filterEvidence;
      const r = await api.listResearchHypotheses(filters);
      if (r?.error) {
        setError(r.message || 'Failed to load hypotheses.');
      } else {
        setHypotheses(Array.isArray(r?.hypotheses) ? r.hypotheses : []);
      }
    } catch (err) {
      setError(err?.message || 'Unexpected error loading hypotheses.');
    } finally {
      setLoading(false);
    }
  }, [filterStatus, filterEvidence]);

  useEffect(() => { loadList(); }, [loadList]);

  // ── Open detail ────────────────────────────────────────────────────────
  const openDetail = async (h) => {
    const api = API();
    setSelected(h);
    setDetail(null);
    if (!api?.getResearchHypothesis) return;
    try {
      const r = await api.getResearchHypothesis(h.id);
      if (!r?.error && r?.hypothesis) {
        setDetail(r.hypothesis);
      } else if (r?.error) {
        setError(r.message || 'Failed to load detail.');
      }
    } catch (err) {
      setError(err?.message || 'Unexpected error.');
    }
  };

  // ── Transition status ──────────────────────────────────────────────────
  const moveTo = async (newStatus) => {
    if (!detail) return;
    const api = API();
    if (!api?.updateResearchHypothesisStatus) return;
    setTransitioning(true);
    try {
      const r = await api.updateResearchHypothesisStatus(detail.id, newStatus);
      if (r?.error) {
        setError(r.message || 'Status update failed.');
      } else {
        // Reload detail + list so badges update everywhere
        await openDetail({ id: detail.id });
        await loadList();
      }
    } catch (err) {
      setError(err?.message || 'Unexpected error during status update.');
    } finally {
      setTransitioning(false);
    }
  };

  const back = () => { setSelected(null); setDetail(null); };

  // #65 Phase C2b-1: the hypothesis detail is this tab's master->detail sub-view.
  // Register ONE Back step so global Back returns to the hypothesis LIST before
  // navigating away — the SAME `back` handler the in-view "Back to list" button
  // uses. Unconditional and above the `if (!selected)` early return (rules-of-
  // hooks). LITERAL label — never a hypothesis title/disease/id (no PHI/data in
  // the sessionStorage-backed nav stack).
  useNavStep({ active: !!selected, label: 'Hypothesis', onBack: back });

  // ─────────────────────────────────────────────────────────────────────
  // Render — list view
  // ─────────────────────────────────────────────────────────────────────
  if (!selected) {
    return (
      <div className="hypotheses-tab">
        <div className="hyp-header">
          <div className="hyp-header-title">
            <FlaskConical size={22} />
            <div>
              <h2>Research Hypotheses</h2>
              <p>Lifecycle-managed L1/L2/L3 hypothesis registry. {hypotheses.length} on file.</p>
            </div>
          </div>
          <button className="hyp-icon-btn" onClick={loadList} title="Refresh">
            <RefreshCw size={16} className={loading ? 'hyp-spin' : ''} />
          </button>
        </div>

        <div className="hyp-filters">
          <div className="hyp-filter-group">
            <Filter size={14} />
            <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
              <option value="">All statuses</option>
              {Object.keys(STATUS_BADGES).map((s) => (
                <option key={s} value={s}>{STATUS_BADGES[s].label}</option>
              ))}
            </select>
            <select value={filterEvidence} onChange={(e) => setFilterEvidence(e.target.value)}>
              <option value="">All evidence grades</option>
              {Object.keys(EVIDENCE_BADGES).map((e) => (
                <option key={e} value={e}>{EVIDENCE_BADGES[e].label}</option>
              ))}
            </select>
          </div>
        </div>

        {error && (
          <div className="hyp-error">
            <AlertTriangle size={16} /> {error}
          </div>
        )}

        {loading && !error && (
          <div className="hyp-loading"><Loader size={16} className="hyp-spin" /> Loading…</div>
        )}

        {!loading && hypotheses.length === 0 && !error && (
          <div className="hyp-empty">
            <FlaskConical size={48} />
            <p>No hypotheses match the current filters.</p>
          </div>
        )}

        {!loading && hypotheses.length > 0 && (
          <div className="hyp-list">
            {hypotheses.map((h) => {
              const ev = EVIDENCE_BADGES[h.evidence_level] || EVIDENCE_BADGES.L3_HYPOTHESIS;
              const st = STATUS_BADGES[h.status] || STATUS_BADGES.proposed;
              const EvIcon = ev.icon;
              return (
                <div key={h.id} className="hyp-row" onClick={() => openDetail(h)}>
                  <div className="hyp-row-body">
                    <div className="hyp-row-title">{h.title}</div>
                    <div className="hyp-row-disease">{h.disease}</div>
                    {h.intent && <div className="hyp-row-intent">{h.intent}</div>}
                  </div>
                  <div className="hyp-row-meta">
                    <span className="hyp-badge" style={{ background: ev.bg, color: ev.color }}>
                      <EvIcon size={11} /> {ev.label}
                    </span>
                    <span className="hyp-badge" style={{ background: st.bg, color: st.color }}>
                      {st.label}
                    </span>
                    <ChevronRight size={16} color="#9CA3AF" />
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {styleBlock()}
      </div>
    );
  }

  // ─────────────────────────────────────────────────────────────────────
  // Render — detail view
  // ─────────────────────────────────────────────────────────────────────
  const h = detail || selected;
  const ev = EVIDENCE_BADGES[h.evidence_level] || EVIDENCE_BADGES.L3_HYPOTHESIS;
  const st = STATUS_BADGES[h.status] || STATUS_BADGES.proposed;
  const EvIcon = ev.icon;
  const components = parseJson(h.components);
  const exclusions = parseJson(h.exclusions);
  const warnings   = parseJson(h.warnings);
  const refs       = parseJson(h.references_list);
  const allowedNext = STATUS_TRANSITIONS[h.status] || [];

  return (
    <div className="hypotheses-tab">
      <div className="hyp-detail-header">
        <button className="hyp-back" onClick={back}>
          <ArrowLeft size={16} /> Back to list
        </button>
        <div className="hyp-detail-actions">
          <span className="hyp-badge" style={{ background: ev.bg, color: ev.color }}>
            <EvIcon size={11} /> {ev.label}
          </span>
          <span className="hyp-badge" style={{ background: st.bg, color: st.color }}>
            {st.label}
          </span>
        </div>
      </div>

      <h2 className="hyp-detail-title">{h.title}</h2>
      <div className="hyp-detail-disease">{h.disease}</div>
      {h.intent && <p className="hyp-detail-intent"><strong>Intent:</strong> {h.intent}</p>}

      {warnings.length > 0 && (
        <div className="hyp-warnings">
          <div className="hyp-warnings-header">
            <AlertTriangle size={16} /> Safety warnings
          </div>
          <ul>{warnings.map((w, i) => <li key={i}>{w}</li>)}</ul>
        </div>
      )}

      {components.length > 0 && (
        <div className="hyp-section">
          <h3>Components ({components.length})</h3>
          {components.map((c, i) => (
            <div key={i} className="hyp-component">
              <div className="hyp-component-id">{c.id}</div>
              {c.mechanism && <div className="hyp-component-mech">{c.mechanism}</div>}
              {(c.fda_status || c.anal_canal_evidence) && (
                <div className="hyp-component-meta">
                  {c.fda_status         && <span>FDA: {c.fda_status}</span>}
                  {c.anal_canal_evidence && <span>Anal canal: {c.anal_canal_evidence}</span>}
                </div>
              )}
              {Array.isArray(c.candidates) && c.candidates.length > 0 && (
                <div className="hyp-component-meta">Candidates: {c.candidates.join(', ')}</div>
              )}
              {Array.isArray(c.platforms) && c.platforms.length > 0 && (
                <div className="hyp-component-meta">Platforms: {c.platforms.join(', ')}</div>
              )}
              {Array.isArray(c.agents) && c.agents.length > 0 && (
                <div className="hyp-component-meta">Agents: {c.agents.join(', ')}</div>
              )}
              {c.agent && <div className="hyp-component-meta">Agent: {c.agent}</div>}
            </div>
          ))}
        </div>
      )}

      {exclusions.length > 0 && (
        <div className="hyp-section">
          <h3>Exclusions</h3>
          <div className="hyp-exclusion-chips">
            {exclusions.map((x, i) => (
              <span key={i} className="hyp-exclusion-chip">
                <XCircle size={12} /> {x.replace(/_/g, ' ')}
              </span>
            ))}
          </div>
        </div>
      )}

      {refs.length > 0 && (
        <div className="hyp-section">
          <h3>References ({refs.length})</h3>
          <ul className="hyp-refs">
            {refs.map((r, i) => (
              <li key={i}>
                {r.label || r.title || JSON.stringify(r)}
                {r.context && <span className="hyp-ref-context"> — {r.context}</span>}
              </li>
            ))}
          </ul>
        </div>
      )}

      {h.pdf_path && (
        <div className="hyp-section">
          <h3>PDF artifact</h3>
          <a className="hyp-pdf-link" href={h.pdf_path} target="_blank" rel="noopener noreferrer">
            <FileText size={14} /> {h.pdf_path} <ExternalLink size={12} />
          </a>
        </div>
      )}

      <div className="hyp-section hyp-meta-grid">
        <div><strong>Source model:</strong> {h.source_model || '—'}</div>
        <div><strong>Created:</strong> {fmtDate(h.created_at)}</div>
        <div><strong>Updated:</strong> {fmtDate(h.updated_at)}</div>
        <div><strong>Created by:</strong> {h.created_by_name || h.created_by || '—'}</div>
        <div><strong>Requires validation:</strong> {h.requires_validation ? 'Yes' : 'No'}</div>
        <div><strong>Clinical trial required:</strong> {h.clinical_trial_required ? 'Yes' : 'No'}</div>
      </div>

      {h.source_notes && (
        <div className="hyp-section">
          <h3>Source notes</h3>
          <p className="hyp-source-notes">{h.source_notes}</p>
        </div>
      )}

      <div className="hyp-transitions">
        <h3>Lifecycle actions</h3>
        {!canWrite && (
          <div className="hyp-warning-readonly">
            <Lock size={14} /> Read-only — you need <code>bi:create</code> to change status.
          </div>
        )}
        {canWrite && allowedNext.length === 0 && (
          <p className="hyp-terminal">This hypothesis is in a terminal state ({st.label}). No further transitions available.</p>
        )}
        {canWrite && allowedNext.length > 0 && (
          <div className="hyp-transition-buttons">
            {allowedNext.map((s) => {
              const target = STATUS_BADGES[s];
              return (
                <button
                  key={s}
                  className="hyp-transition-btn"
                  disabled={transitioning}
                  onClick={() => moveTo(s)}
                  style={{ background: target.bg, color: target.color, borderColor: target.color }}
                >
                  → {target.label}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {error && (
        <div className="hyp-error">
          <AlertTriangle size={16} /> {error}
        </div>
      )}

      {styleBlock()}
    </div>
  );
}

// ─── Helpers ──────────────────────────────────────────────────────────────
function parseJson(v) {
  if (Array.isArray(v)) return v;
  if (typeof v === 'string') {
    try { const x = JSON.parse(v); return Array.isArray(x) ? x : []; } catch { return []; }
  }
  return [];
}
function fmtDate(d) {
  if (!d) return '—';
  const dt = new Date(d);
  return Number.isNaN(dt.getTime()) ? d : dt.toLocaleString();
}

function styleBlock() {
  return (
    <style jsx>{`
      .hypotheses-tab { padding: 4px 0 24px; }
      .hyp-header {
        display: flex; justify-content: space-between; align-items: flex-start;
        gap: 12px; margin-bottom: 12px;
      }
      .hyp-header-title { display: flex; gap: 12px; align-items: flex-start; color: #2E8B98; }
      .hyp-header-title h2 { margin: 0; font-size: 18px; color: #0B2A4A; }
      .hyp-header-title p  { margin: 2px 0 0; font-size: 12px; color: #6C757D; }
      .hyp-icon-btn {
        background: white; border: 1px solid #E8ECF1; border-radius: 6px;
        padding: 6px 8px; cursor: pointer; color: #6C757D;
      }
      .hyp-icon-btn:hover { color: #2E8B98; background: #F1F5F9; }
      .hyp-spin { animation: hyp-rot 1s linear infinite; }
      @keyframes hyp-rot { from { transform: rotate(0deg);} to { transform: rotate(360deg);} }
      .hyp-filters { margin-bottom: 12px; }
      .hyp-filter-group {
        display: flex; gap: 8px; align-items: center;
        background: #F8F9FA; border: 1px solid #E8ECF1; border-radius: 8px;
        padding: 8px 10px;
      }
      .hyp-filter-group select {
        border: 1px solid #E8ECF1; border-radius: 6px; padding: 4px 8px;
        font-size: 13px; background: white; color: #212529;
      }
      .hyp-list {
        background: white; border: 1px solid #E8ECF1; border-radius: 12px; overflow: hidden;
      }
      .hyp-row {
        display: grid; grid-template-columns: 1fr auto;
        gap: 12px; padding: 14px 16px;
        border-bottom: 1px solid #E8ECF1; cursor: pointer;
        transition: background 0.15s;
      }
      .hyp-row:last-child { border-bottom: none; }
      .hyp-row:hover { background: #F8F9FA; }
      .hyp-row-body { min-width: 0; }
      .hyp-row-title { font-weight: 600; color: #0B2A4A; margin-bottom: 2px; }
      .hyp-row-disease { font-size: 12px; color: #6C757D; }
      .hyp-row-intent { font-size: 12px; color: #475569; margin-top: 4px; line-height: 1.4; }
      .hyp-row-meta { display: flex; gap: 6px; align-items: center; }
      .hyp-badge {
        display: inline-flex; align-items: center; gap: 4px;
        padding: 3px 8px; border-radius: 10px;
        font-size: 11px; font-weight: 600;
      }
      .hyp-loading, .hyp-empty {
        padding: 40px; text-align: center; color: #6C757D;
      }
      .hyp-empty svg { color: #d1d5db; margin-bottom: 12px; }
      .hyp-error {
        display: flex; gap: 8px; align-items: center;
        background: #FEE2E2; color: #991B1B; padding: 10px 12px;
        border-radius: 8px; margin: 8px 0; font-size: 13px;
      }
      .hyp-detail-header {
        display: flex; justify-content: space-between; align-items: center;
        margin-bottom: 16px;
      }
      .hyp-back {
        display: inline-flex; gap: 6px; align-items: center;
        background: none; border: none; color: #2E8B98;
        cursor: pointer; font-size: 13px; font-weight: 500; padding: 4px 0;
      }
      .hyp-back:hover { text-decoration: underline; }
      .hyp-detail-actions { display: flex; gap: 8px; }
      .hyp-detail-title {
        margin: 0 0 4px; font-size: 22px; color: #0B2A4A; line-height: 1.25;
      }
      .hyp-detail-disease {
        color: #6C757D; font-size: 13px; margin-bottom: 12px;
      }
      .hyp-detail-intent {
        background: #F8F9FA; border-left: 3px solid #2E8B98;
        padding: 10px 14px; font-size: 14px; color: #212529;
        border-radius: 4px; margin: 0 0 16px;
      }
      .hyp-warnings {
        background: #FEF3C7; border: 1px solid #92400E; border-radius: 8px;
        padding: 12px 16px; margin-bottom: 16px;
      }
      .hyp-warnings-header {
        display: flex; gap: 6px; align-items: center;
        color: #92400E; font-weight: 600; font-size: 13px; margin-bottom: 6px;
      }
      .hyp-warnings ul { margin: 0; padding-left: 18px; }
      .hyp-warnings li { color: #78350F; font-size: 13px; margin-bottom: 4px; line-height: 1.45; }
      .hyp-section { margin-bottom: 20px; }
      .hyp-section h3 {
        font-size: 13px; color: #475569; text-transform: uppercase;
        letter-spacing: 0.5px; margin: 0 0 8px; font-weight: 600;
      }
      .hyp-component {
        background: white; border: 1px solid #E8ECF1; border-radius: 8px;
        padding: 10px 12px; margin-bottom: 8px;
      }
      .hyp-component-id {
        font-family: ui-monospace, Menlo, monospace; font-size: 12.5px;
        color: #0B2A4A; font-weight: 600; margin-bottom: 4px;
      }
      .hyp-component-mech { color: #475569; font-size: 13px; line-height: 1.4; }
      .hyp-component-meta {
        display: flex; gap: 12px; flex-wrap: wrap; margin-top: 4px;
        font-size: 11.5px; color: #6C757D;
      }
      .hyp-component-meta span:after { content: ''; }
      .hyp-exclusion-chips { display: flex; flex-wrap: wrap; gap: 6px; }
      .hyp-exclusion-chip {
        display: inline-flex; gap: 4px; align-items: center;
        background: #FEE2E2; color: #991B1B;
        padding: 3px 8px; border-radius: 10px;
        font-size: 11.5px; font-weight: 500;
      }
      .hyp-refs { margin: 0; padding-left: 18px; }
      .hyp-refs li { color: #212529; font-size: 13px; margin-bottom: 4px; line-height: 1.45; }
      .hyp-ref-context { color: #6C757D; font-size: 12px; }
      .hyp-pdf-link {
        display: inline-flex; gap: 6px; align-items: center;
        color: #2E8B98; font-size: 13px; text-decoration: none;
        padding: 8px 12px; background: #ECFEFF; border: 1px solid #67E8F9;
        border-radius: 6px;
      }
      .hyp-pdf-link:hover { background: #CFFAFE; }
      .hyp-meta-grid {
        display: grid; grid-template-columns: repeat(2, 1fr); gap: 8px 24px;
        font-size: 13px; color: #475569;
      }
      .hyp-source-notes {
        background: #F8F9FA; padding: 10px 12px;
        border-radius: 6px; font-size: 13px;
        color: #475569; line-height: 1.5;
      }
      .hyp-transitions {
        background: #F8F9FA; padding: 16px; border-radius: 8px;
        border: 1px solid #E8ECF1; margin-top: 16px;
      }
      .hyp-transition-buttons { display: flex; gap: 8px; flex-wrap: wrap; }
      .hyp-transition-btn {
        padding: 8px 14px; border: 1px solid;
        border-radius: 6px; font-size: 13px; font-weight: 600;
        cursor: pointer; transition: opacity 0.2s;
      }
      .hyp-transition-btn:disabled { opacity: 0.5; cursor: not-allowed; }
      .hyp-warning-readonly {
        display: flex; gap: 6px; align-items: center;
        color: #6C757D; font-size: 13px;
      }
      .hyp-warning-readonly code {
        background: white; padding: 1px 5px; border-radius: 3px;
        font-size: 12px;
      }
      .hyp-terminal { color: #6C757D; font-size: 13px; margin: 0; }
    `}</style>
  );
}
